import React, { useContext, useState } from 'react'
import { ChannelContext, channelForMasterDealer } from '@monorepo/infra'
import * as S from './styles'

/**
 * Asks a dealer which master dealer they are shopping from, before the
 * storefront loads.
 *
 * A person can belong to several master dealers with a different standing in
 * each - principal at one, ordinary team member at another - and that decides
 * what they may order, Co-Op included. The storefront used to default silently
 * to whichever came back first, with no way to switch, so anyone with a second
 * one simply could not reach it.
 *
 * The question is the master dealer, never the channel: channels are switched
 * inside the store, and asking per channel would have put five rows naming the
 * same company in front of anyone on a master dealer like 2251.
 *
 * Rendered only while `needsAccountSelection` is true - a dealer with a single
 * master dealer is never interrupted, and the choice is remembered per user.
 */
const AccountSelector: React.FC = () => {
  const { masterDealers, needsAccountSelection, selectedChannel, setState } =
    useContext(ChannelContext)
  const [chosenId, setChosenId] = useState<string | null>(null)

  if (!needsAccountSelection) {
    return null
  }

  const chosen = masterDealers.find((m) => m.masterDealerId === chosenId)

  const confirm = () => {
    if (!chosen) return
    // Keep the channel they are already in where this master dealer offers it,
    // so choosing an account does not quietly move them somewhere else too.
    const channel = channelForMasterDealer(chosen, selectedChannel)
    if (channel) setState(chosen.masterDealerId, channel)
  }

  return (
    <S.Overlay role="dialog" aria-modal="true" aria-label="Choose an account">
      <S.Panel>
        <S.Title>Which account are you shopping from?</S.Title>
        <S.Subtitle>
          Your email is registered to more than one dealer account. Your choice
          decides what you can order, including Co-Op. You can switch accounts
          later from the dealer code at the top of the page.
        </S.Subtitle>

        <S.List>
          {masterDealers.map((masterDealer) => {
            const id = masterDealer.masterDealerId
            return (
              <S.Option
                key={id}
                type="button"
                selected={id === chosenId}
                aria-pressed={id === chosenId}
                onClick={() => setChosenId(id)}
              >
                <S.OptionName>{masterDealer.masterDealerName}</S.OptionName>
                <S.OptionMeta>{id}</S.OptionMeta>
                {masterDealer.isPrincipal && (
                  <S.PrincipalTag>Principal</S.PrincipalTag>
                )}
              </S.Option>
            )
          })}
        </S.List>

        <S.Actions>
          <S.Continue type="button" disabled={!chosen} onClick={confirm}>
            Continue
          </S.Continue>
        </S.Actions>
      </S.Panel>
    </S.Overlay>
  )
}

export default AccountSelector
